<template>
    <Tooltip :is-disabled="!props.tooltipText && !props.disabledExplanation" :is-teleported="true">
        <template #trigger="tooltipProps">
            <label
                class="min-w-max flex flex-row items-center rounded-full text-gray-700 font-light cursor-pointer"
                :class="[
                    props.isActive ? 'bg-sky-100' : 'bg-gray-100',
                    { ' hover:cursor-not-allowed': isDisabled },
                    sizeClassesWrapper,
                ]"
                :aria-describedby="tooltipProps['aria-labelledby']"
                :popovertarget="tooltipProps.popovertarget"
            >
                <button
                    type="button"
                    class="inline-flex shrink-0 rounded-full border-2 border-transparent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-700 focus-visible:ring-offset-2"
                    :class="[
                        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                        {
                            'bg-sky-700': props.isActive,
                            'bg-gray-200': !props.isActive && !isDisabled,
                            'bg-gray-300': !props.isActive && isDisabled,
                            'active:bg-gray-700  transition-colors duration-200 ease-in-out': !isDisabled,
                        },
                        toggleSizeClasses,
                    ]"
                    role="switch"
                    :aria-checked="props.isActive"
                    :aria-disabled="isDisabled"
                    @click="(event: Event) => (isDisabled ? undefined : emit('toggle', event))"
                >
                    <span
                        aria-hidden="true"
                        class="pointer-events-none inline-block transform rounded-full shadow-sm ring-0 transition duration-200 ease-in-out"
                        :class="[
                            props.isActive ? 'translate-x-4' : 'translate-x-0',
                            isDisabled ? 'bg-gray-400' : 'bg-white',
                            toggleInnerSizeClasses,
                        ]"
                    />
                </button>

                <span class="text-right flex-1" :class="sizeClassesText">
                    <slot />
                </span>
            </label>
        </template>

        <template #text>
            <template v-if="props.disabledExplanation">{{ props.disabledExplanation }}</template>
            <template v-if="!props.disabledExplanation">{{ props.tooltipText }}</template>
        </template>
    </Tooltip>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tooltip from '../Tooltip/Tooltip.vue';

export type ActivatedToggleSize = 'Extra-Small' | 'Small'; // Extra-Small = 24px height, Small = 32px height

type ActivatedToggleProps = {
    isActive: boolean;
    tooltipText?: string;
    disabledExplanation?: string;
    size: ActivatedToggleSize;
};

type ActivatedToggleEmits = {
    (e: 'toggle', event: Event): void;
};

const props = defineProps<ActivatedToggleProps>();
const emit = defineEmits<ActivatedToggleEmits>();

const isDisabled = computed(() => Boolean(props.disabledExplanation));

const sizeClassesWrapper = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return 'pl-1';

        case 'Small':
            return 'pl-1.5';

        default:
            throw new Error(`Invalid size prop: ${props.size}`);
    }
});

const sizeClassesText = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return 'py-0.5 text-sm pl-1.5 pr-2';

        case 'Small':
            return 'py-1 text-base pl-2 pr-2.5';

        default:
            throw new Error(`Invalid size prop: ${props.size}`);
    }
});

const toggleSizeClasses = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return 'h-4 w-8';

        case 'Small':
            return 'h-5 w-9';

        default:
            throw new Error(`Invalid size prop: ${props.size}`);
    }
});

const toggleInnerSizeClasses = computed(() => {
    switch (props.size) {
        case 'Extra-Small':
            return 'size-3';

        case 'Small':
            return 'size-4';

        default:
            throw new Error(`Invalid size prop: ${props.size}`);
    }
});
</script>
